import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Search,
  QrCode,
  Copy,
  CheckCircle,
  Trash2,
  Eye,
  XCircle,
  User,
  Phone,
  DollarSign,
  Calendar,
  CreditCard,
  Package,
  Info,
} from "lucide-react";
import PixReceivedModal from "./PixReceivedModal";
import PixQrCodeModal from "./PixQrCodeModal";
import PaymentDetailsModal from "./PaymentDetailsModal";
import pixService from "@/services/pixService";
import { showError, showInfo } from "@/store/errorStore";

// Card de Pagamento Responsivo (genérico para todos os tipos)
const PaymentCard = ({
  payment,
  onViewQRCode,
  onCopy,
  onMarkReceived,
  onDelete,
}) => {
  const [expanded, setExpanded] = useState(false);

  const isReceived =
    payment.status === "S" || payment.recebido || payment.status === "RECEIVED";

  const getStatusColor = () => {
    return isReceived
      ? "bg-green-100 text-green-800 border-green-200"
      : "bg-yellow-100 text-yellow-800 border-yellow-200";
  };

  const getPaymentIcon = (method) => {
    const methodLower = (method || "").toLowerCase();
    if (methodLower.includes("pix")) return <QrCode className="h-3 w-3" />;
    if (
      methodLower.includes("cartão") ||
      methodLower.includes("cartao") ||
      methodLower.includes("card")
    )
      return <CreditCard className="h-3 w-3" />;
    return <DollarSign className="h-3 w-3" />;
  };

  return (
    <Card
      className="overflow-hidden hover:shadow-lg transition-all duration-200 border-l-4"
      style={{ borderLeftColor: isReceived ? "#10b981" : "#f59e0b" }}
    >
      <CardHeader className="pb-3 bg-gradient-to-r from-gray-50 to-gray-100">
        <div className="flex justify-between items-start gap-2">
          <div className="space-y-1 flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {payment.txid && (
                <Badge
                  variant="outline"
                  className="text-xs md:text-sm font-mono border-[#c9332C] text-[#c9332C]"
                >
                  {payment.txid.substring(0, 12)}...
                </Badge>
              )}
              <Badge
                className={`text-xs md:text-sm border ${getStatusColor()}`}
              >
                {isReceived ? "Pago" : "Pendente"}
              </Badge>
              {payment.pay_method && (
                <Badge className="text-xs md:text-sm bg-blue-600 text-white border-0">
                  {getPaymentIcon(payment.pay_method)}
                  <span className="ml-1">{payment.pay_method}</span>
                </Badge>
              )}
            </div>
            {payment.orderCode && (
              <p className="text-sm md:text-base font-semibold text-blue-600 flex items-center gap-1">
                <Package className="h-4 w-4" />
                Pedido #{payment.orderCode}
              </p>
            )}
            {payment.name && (
              <p className="text-xs md:text-sm text-gray-600 truncate flex items-center gap-1">
                <User className="h-3 w-3" />
                {payment.name}
              </p>
            )}
          </div>
          <div className="text-right space-y-1">
            <div className="text-lg md:text-xl font-bold text-green-600">
              {pixService.formatCurrency(payment.amount || payment.valor || 0)}
            </div>
            {payment.createdAt && (
              <p className="text-xs text-gray-500">
                {pixService.formatDate(payment.createdAt)}
              </p>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-3 pb-3 space-y-3">
        {/* Info Grid - Sempre Visível */}
        <div className="grid grid-cols-2 gap-2">
          {payment.orderCode && (
            <div className="bg-indigo-50 p-2 rounded border border-indigo-200">
              <p className="text-xs text-indigo-600 font-semibold flex items-center gap-1">
                <Package className="h-3 w-3" />
                Pedido
              </p>
              <p className="text-xs md:text-sm font-bold text-gray-900 truncate">
                #{payment.orderCode}
              </p>
            </div>
          )}
          {payment.pay_method && (
            <div className="bg-blue-50 p-2 rounded border border-blue-200">
              <p className="text-xs text-blue-600 font-semibold flex items-center gap-1">
                <CreditCard className="h-3 w-3" />
                Pagamento
              </p>
              <p className="text-xs md:text-sm font-bold text-gray-900 truncate">
                {payment.pay_method}
              </p>
            </div>
          )}
          {payment.name && (
            <div className="bg-green-50 p-2 rounded border border-green-200">
              <p className="text-xs text-green-600 font-semibold flex items-center gap-1">
                <User className="h-3 w-3" />
                Cliente
              </p>
              <p className="text-xs md:text-sm font-bold text-gray-900 truncate">
                {payment.name}
              </p>
            </div>
          )}
          {payment.contact && (
            <div className="bg-teal-50 p-2 rounded border border-teal-200">
              <p className="text-xs text-teal-600 font-semibold flex items-center gap-1">
                <Phone className="h-3 w-3" />
                Contato
              </p>
              <p className="text-xs md:text-sm font-bold text-gray-900 truncate">
                {pixService.formatPhone(payment.contact)}
              </p>
            </div>
          )}
          <div className="bg-purple-50 p-2 rounded border border-purple-200">
            <p className="text-xs text-purple-600 font-semibold flex items-center gap-1">
              <DollarSign className="h-3 w-3" />
              Valor
            </p>
            <p className="text-xs md:text-sm font-bold text-gray-900">
              {pixService.formatCurrency(payment.amount || payment.valor || 0)}
            </p>
          </div>
          {payment.createdAt && (
            <div className="bg-amber-50 p-2 rounded border border-amber-200">
              <p className="text-xs text-amber-600 font-semibold flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Criado em
              </p>
              <p className="text-xs md:text-sm font-bold text-gray-900">
                {pixService.formatDate(payment.createdAt)?.split(" ")[0] || "-"}
              </p>
            </div>
          )}
        </div>

        {/* ID da Transação (se existir) */}
        {payment.txid && (
          <div className="bg-gray-50 p-2 rounded border border-gray-200">
            <p className="text-xs text-gray-600 font-semibold mb-1">
              ID da Transação
            </p>
            <p className="text-xs font-mono text-gray-900 break-all">
              {payment.txid}
            </p>
          </div>
        )}

        {/* Ações Rápidas */}
        <div className="flex flex-col gap-2 pt-2 border-t">
          <div className="grid grid-cols-2 gap-2">
            {(payment.qrcodeUrl || payment.qrcode || payment.imagemQrcode) && (
              <Button
                variant="outline"
                size="default"
                className="text-sm md:text-base text-blue-600 hover:bg-blue-50 border-blue-300 h-11 md:h-12"
                onClick={() => onViewQRCode(payment)}
              >
                <Eye className="h-4 w-4 mr-1" />
                Ver QR
              </Button>
            )}
            {payment.txid && (
              <Button
                variant="outline"
                size="default"
                className="text-sm md:text-base text-green-600 hover:bg-green-50 border-green-300 h-11 md:h-12"
                onClick={() => onCopy(payment)}
              >
                <Copy className="h-4 w-4 mr-1" />
                Copiar
              </Button>
            )}
          </div>

          {/* Botão de Expandir Ações */}
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-xs text-gray-600"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <>
                <ChevronUp className="h-4 w-4 mr-1" />
                Menos ações
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4 mr-1" />
                Mais ações
              </>
            )}
          </Button>

          {/* Ações expandidas */}
          {expanded && (
            <div className="space-y-2 pt-2 border-t">
              <div className="grid grid-cols-1 gap-2">
                {!isReceived && (
                  <Button
                    variant="outline"
                    size="default"
                    className="text-sm md:text-base text-green-600 hover:bg-green-50 h-12 md:h-14 font-semibold"
                    onClick={() => onMarkReceived(payment)}
                  >
                    <CheckCircle className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                    Marcar como Recebido
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="default"
                  className="text-sm md:text-base text-red-600 hover:bg-red-50 h-12 md:h-14 font-semibold"
                  onClick={() => onDelete(payment)}
                >
                  <Trash2 className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                  Excluir Pagamento
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Componente de gerenciamento de PIX gerados
 */
export default function PixManagement() {
  const [pixList, setPixList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPix, setSelectedPix] = useState(null);
  const [showReceivedModal, setShowReceivedModal] = useState(false);
  const [confirmingReceived, setConfirmingReceived] = useState(false);
  const [deletingTxid, setDeletingTxid] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showQrCodeModal, setShowQrCodeModal] = useState(false);
  const [selectedPixForQrCode, setSelectedPixForQrCode] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedPaymentForDetails, setSelectedPaymentForDetails] =
    useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const PAGE_SIZE = 10;

  useEffect(() => {
    loadData();
  }, []);

  /**
   * Carregar PIX gerados
   */
  const loadData = async () => {
    setLoading(true);
    setLoadingMessage("Carregando PIX gerados...");
    try {
      const data = await pixService.getAllPix();
      setPixList(Array.isArray(data) ? data : []);
    } catch (error) {
      showError(
        error.response?.data?.message || "Erro ao carregar PIX",
        "Erro de Carregamento"
      );
      setPixList([]);
    } finally {
      setLoading(false);
      setLoadingMessage("");
    }
  };

  /**
   * Buscar todos os PIX
   */
  const fetchPix = async () => {
    setLoading(true);
    setLoadingMessage("Atualizando lista de PIX...");
    try {
      const data = await pixService.getAllPix();
      setPixList(Array.isArray(data) ? data : []);
    } catch (error) {
      showError(
        error.response?.data?.message || "Erro ao carregar PIX",
        "Erro de Carregamento"
      );
      setPixList([]);
    } finally {
      setLoading(false);
      setLoadingMessage("");
    }
  };

  /**
   * Abrir modal para confirmar recebimento
   */
  const handleMarkAsReceived = (pix) => {
    setSelectedPix(pix);
    setShowReceivedModal(true);
  };

  /**
   * Confirmar recebimento do PIX
   */
  const handleConfirmReceived = async () => {
    if (!selectedPix) return;

    setLoading(true);
    setLoadingMessage(`Marcando PIX como recebido...`);
    setConfirmingReceived(true);
    try {
      await pixService.markAsReceived(selectedPix.txid);
      showInfo("PIX marcado como recebido com sucesso", "PIX Recebido");
      setShowReceivedModal(false);
      setSelectedPix(null);
      await fetchPix();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Erro ao marcar PIX como recebido";
      showError(errorMessage, "Erro");
    } finally {
      setConfirmingReceived(false);
      setLoading(false);
      setLoadingMessage("");
    }
  };

  /**
   * Confirmar exclusão de PIX
   */
  const handleDeleteClick = (pix) => {
    setDeletingTxid(pix.txid);
    setShowDeleteDialog(true);
  };

  /**
   * Executar exclusão de PIX
   */
  const handleConfirmDelete = async () => {
    if (!deletingTxid) return;

    setLoading(true);
    setLoadingMessage("Excluindo PIX...");
    try {
      await pixService.deletePix(deletingTxid);
      showInfo("PIX removido com sucesso", "PIX Deletado");
      await fetchPix();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Erro ao deletar PIX";
      showError(errorMessage, "Erro");
    } finally {
      setShowDeleteDialog(false);
      setDeletingTxid(null);
      setLoading(false);
      setLoadingMessage("");
    }
  };

  /**
   * Copiar payload do PIX
   */
  const handleCopyPayload = async (pix) => {
    try {
      await pixService.copyToClipboard(pix.payload || pix.pixCopiaECola || "");
      showInfo("Código PIX copiado para área de transferência", "Copiado");
    } catch (error) {
      showError("Erro ao copiar código PIX", "Erro");
    }
  };

  /**
   * Visualizar QR Code
   */
  const handleViewQRCode = (pix) => {
    setSelectedPixForQrCode(pix);
    setShowQrCodeModal(true);
  };

  /**
   * Filtrar pagamentos por busca
   */
  const filteredPix = pixList.filter((payment) => {
    const query = searchQuery.toLowerCase();
    const txid = (payment.txid || "").toLowerCase();
    const amount = (payment.amount || payment.valor || 0).toString();
    const name = (payment.name || "").toLowerCase();
    const orderCode = (payment.orderCode || "").toLowerCase();
    const payMethod = (payment.pay_method || "").toLowerCase();

    return (
      txid.includes(query) ||
      amount.includes(query) ||
      name.includes(query) ||
      orderCode.includes(query) ||
      payMethod.includes(query)
    );
  });

  /**
   * Resetar página quando o filtro mudar
   */
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  /**
   * Cálculos de paginação
   */
  const totalPages = Math.ceil(filteredPix.length / PAGE_SIZE);
  const pageStartIndex = (currentPage - 1) * PAGE_SIZE;
  const paginatedPix = filteredPix.slice(
    pageStartIndex,
    pageStartIndex + PAGE_SIZE
  );

  /**
   * Calcular estatísticas
   */
  const calculateStats = () => {
    const totalPix = pixList.length;
    const totalReceived = pixList.filter(
      (p) => p.status === "S" || p.recebido || p.status === "RECEIVED"
    ).length;
    const totalPending = totalPix - totalReceived;
    const totalValue = pixList.reduce(
      (sum, p) => sum + (p.amount || p.valor || 0),
      0
    );
    const receivedValue = pixList
      .filter((p) => p.status === "S" || p.recebido || p.status === "RECEIVED")
      .reduce((sum, p) => sum + (p.amount || p.valor || 0), 0);

    return {
      totalPix,
      totalReceived,
      totalPending,
      totalValue,
      receivedValue,
    };
  };

  const stats = calculateStats();

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
          Pagamentos Gerados
        </h2>
        <Badge variant="outline" className="text-base md:text-lg px-3 py-1">
          Total: <span className="font-bold ml-1">{filteredPix.length}</span>
        </Badge>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <Card>
          <CardHeader className="pb-2 md:pb-3">
            <CardTitle className="text-xs md:text-sm font-medium text-gray-600 flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Total
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl md:text-2xl font-bold">
              {stats.totalPix}
            </div>
            <p className="text-xs text-gray-500 mt-1">Pagamentos</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 md:pb-3">
            <CardTitle className="text-xs md:text-sm font-medium text-gray-600 flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Recebidos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl md:text-2xl font-bold text-green-600">
              {stats.totalReceived}
            </div>
            <p className="text-xs text-gray-500 mt-1">Pagos</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 md:pb-3">
            <CardTitle className="text-xs md:text-sm font-medium text-gray-600 flex items-center gap-2">
              <XCircle className="h-4 w-4 text-yellow-600" />
              Pendentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl md:text-2xl font-bold text-yellow-600">
              {stats.totalPending}
            </div>
            <p className="text-xs text-gray-500 mt-1">Aguardando</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 md:pb-3">
            <CardTitle className="text-xs md:text-sm font-medium text-gray-600 flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Valor Total
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg md:text-xl font-bold">
              {pixService.formatCurrency(stats.totalValue)}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Recebido: {pixService.formatCurrency(stats.receivedValue)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Busca */}
      <Card>
        <CardContent className="p-3 md:p-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar por pedido, método, cliente, valor ou ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10 h-11 md:h-12 text-sm md:text-base"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-4 w-4" />
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabela de Pagamentos */}
      <Card>
        <CardContent className="p-0">
          {filteredPix.length === 0 ? (
            <div className="text-center py-12">
              <CreditCard className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500 mb-2 text-base md:text-lg">
                {searchQuery
                  ? "Nenhum pagamento encontrado"
                  : "Nenhum pagamento gerado"}
              </p>
              <p className="text-gray-400 text-sm">
                {searchQuery
                  ? "Tente outro termo de busca"
                  : "Os pagamentos gerados aparecerão aqui"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[120px]">Pedido</TableHead>
                    <TableHead className="hidden md:table-cell">
                      Cliente
                    </TableHead>
                    <TableHead className="hidden lg:table-cell">
                      ID Transação
                    </TableHead>
                    <TableHead className="hidden md:table-cell text-center">
                      Status
                    </TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead className="text-right w-[100px]">
                      Ações
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedPix.map((payment) => {
                    const isReceived =
                      payment.status === "S" ||
                      payment.recebido ||
                      payment.status === "RECEIVED";

                    return (
                      <TableRow
                        key={payment.txid || payment.orderCode || Math.random()}
                        className="hover:bg-gray-50 cursor-pointer"
                      >
                        <TableCell>
                          <div>
                            <div className="font-medium text-blue-600">
                              #{payment.orderCode || "N/A"}
                            </div>
                            {/* Info extra visível apenas no mobile */}
                            <div className="md:hidden text-xs text-gray-500 mt-1">
                              <div className="truncate">{payment.name}</div>
                              {payment.txid && (
                                <div className="font-mono">
                                  {payment.txid.substring(0, 12)}...
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <div>
                            <div className="font-medium truncate max-w-[200px]">
                              {payment.name || "-"}
                            </div>
                            {payment.contact && (
                              <div className="text-xs text-gray-500">
                                {pixService.formatPhone(payment.contact)}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          {payment.txid ? (
                            <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                              {payment.txid.substring(0, 12)}...
                            </code>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-center">
                          {isReceived ? (
                            <Badge className="bg-green-100 text-green-800 border-green-200">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Pago
                            </Badge>
                          ) : (
                            <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
                              <XCircle className="h-3 w-3 mr-1" />
                              Pendente
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="font-bold text-green-600">
                            {pixService.formatCurrency(
                              payment.amount || payment.valor || 0
                            )}
                          </div>
                          {payment.createdAt && (
                            <div className="text-xs text-gray-500">
                              {pixService
                                .formatDate(payment.createdAt)
                                ?.split(" ")[0] || "-"}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-[#c9332C] hover:text-[#a02923] hover:bg-red-50"
                            onClick={() => {
                              setSelectedPaymentForDetails(payment);
                              setShowDetailsModal(true);
                            }}
                          >
                            <Info className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Paginação */}
      {filteredPix.length > 0 && (
        <Card>
          <CardContent className="p-4 md:p-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-600">
                Mostrando{" "}
                <span className="font-semibold text-gray-900">
                  {pageStartIndex + 1}-
                  {Math.min(pageStartIndex + PAGE_SIZE, filteredPix.length)}
                </span>{" "}
                de{" "}
                <span className="font-semibold text-gray-900">
                  {filteredPix.length}
                </span>{" "}
                pagamentos
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="h-10 md:h-12 px-4 md:px-6"
                >
                  Anterior
                </Button>
                <div className="flex items-center px-4 text-sm font-medium text-gray-700">
                  Página {currentPage} de {totalPages}
                </div>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="h-10 md:h-12 px-4 md:px-6"
                >
                  Próxima
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modais */}
      <PaymentDetailsModal
        open={showDetailsModal}
        onOpenChange={setShowDetailsModal}
        payment={selectedPaymentForDetails}
        onViewQRCode={handleViewQRCode}
        onCopy={handleCopyPayload}
        onMarkReceived={handleMarkAsReceived}
        onDelete={handleDeleteClick}
      />

      <PixReceivedModal
        open={showReceivedModal}
        onOpenChange={setShowReceivedModal}
        pix={selectedPix}
        onConfirm={handleConfirmReceived}
        loading={confirmingReceived}
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este pagamento? Esta ação não pode
              ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletingTxid(null)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PixQrCodeModal
        open={showQrCodeModal}
        onOpenChange={setShowQrCodeModal}
        pix={selectedPixForQrCode}
      />

      {/* Modal de Loading Global */}
      <Dialog open={loading} onOpenChange={() => {}}>
        <DialogContent
          className="max-w-sm"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <div className="flex flex-col items-center justify-center py-8">
            <div className="relative">
              {/* Spinner animado */}
              <div className="w-16 h-16 border-4 border-gray-200 border-t-[#c9332C] rounded-full animate-spin"></div>
              {/* Ícone central */}
              <div className="absolute inset-0 flex items-center justify-center">
                <QrCode className="h-6 w-6 text-[#c9332C]" />
              </div>
            </div>
            <p className="mt-6 text-base font-semibold text-gray-900 text-center">
              {loadingMessage || "Processando..."}
            </p>
            <p className="mt-2 text-sm text-gray-500 text-center">
              Por favor, aguarde
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
